import React from 'react';
import { Zap, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const WeeklyDetailPanel = ({ weeklyData, onNavigate }) => {
  const { t } = useTranslation();

  // Use REAL weeklyData or fallback to fake data
  const defaultDays = [
    { day: t('dashboard.weeklyProgress.days.mon'), lessons: 3, minutes: 45, goalMet: true },
    { day: t('dashboard.weeklyProgress.days.tue'), lessons: 5, minutes: 60, goalMet: true },
    { day: t('dashboard.weeklyProgress.days.wed'), lessons: 2, minutes: 30, goalMet: false },
    { day: t('dashboard.weeklyProgress.days.thu'), lessons: 4, minutes: 50, goalMet: true },
    { day: t('dashboard.weeklyProgress.days.fri'), lessons: 0, minutes: 0, goalMet: false },
    { day: t('dashboard.weeklyProgress.days.sat'), lessons: 6, minutes: 75, goalMet: true },
    { day: t('dashboard.weeklyProgress.days.sun'), lessons: 3, minutes: 40, goalMet: false }
  ];
  const days = weeklyData && weeklyData.length > 0 ? weeklyData : defaultDays;

  const totalMinutes = days.reduce((sum, d) => sum + (d.minutes || 0), 0);
  const activeDays = days.filter(d => d.lessons > 0).length;
  const totalLessons = days.reduce((sum, d) => sum + (d.lessons || 0), 0);

  return (
    <>
      {/* Weekly Summary */}
      <div
        className="group relative bg-black text-white rounded-2xl p-6 overflow-hidden border-[3px] border-black"
        style={{
          animation: 'slideInScale 0.5s ease-out',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#3A86FF] opacity-20 rounded-full transform -translate-y-16 translate-x-16" />

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/20" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/20" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-6 h-6 text-[#3A86FF]" />
            <h3 className="font-space text-2xl font-black tracking-tight">{t('dashboard.weeklyProgress.title')}</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-3 border border-white/20">
              <p className="text-xs text-white/60 mb-1 uppercase tracking-wider font-medium">{t('stats.main.studyTime')}</p>
              <p className="font-space text-2xl font-black">
                {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 border border-white/20">
              <p className="text-xs text-white/60 mb-1 uppercase tracking-wider font-medium">{t('dashboard.weeklyProgress.daysActiveLabel')}</p>
              <p className="font-space text-2xl font-black">{activeDays}/7</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 border border-white/20">
              <p className="text-xs text-white/60 mb-1 uppercase tracking-wider font-medium">{t('stats.main.lessons')}</p>
              <p className="font-space text-2xl font-black">{totalLessons}</p>
            </div>
          </div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      {/* Day by Day Breakdown */}
      <div className="space-y-3">
        {days.map((dayData, i) => {
          const active = dayData.lessons > 0;
          const minutes = dayData.minutes || 0;
          // Calculate bar width based on minutes (max 120 minutes = 100% width for visualization)
          const maxMinutes = 120; // 2 hours per day max for visualization
          const barWidth = minutes > 0 ? Math.min((minutes / maxMinutes) * 100, 100) : 0;
          
          return (
            <div
              key={i}
              className={`group relative rounded-xl p-5 transition-all duration-300 overflow-hidden ${
                active 
                  ? 'bg-white border-[3px] border-black/15 hover:border-[#3A86FF] hover:shadow-xl' 
                  : 'bg-gray-100 border-[3px] border-gray-200'
              }`}
              style={{
                animation: `slideInScale 0.5s ease-out ${i * 0.05}s both`
              }}
            >
              {/* Corner decorations */}
              {active && (
                <>
                  <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                </>
              )}

              <div className="relative z-10">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h5 className={`font-space font-black text-lg ${active ? 'text-black' : 'text-gray-400'}`}>
                      {dayData.day}
                    </h5>
                    {active ? (
                      <p className="text-sm text-gray-600 mt-1 font-medium">
                        {t('dashboard.weeklyProgress.tooltip.lessons', { count: dayData.lessons })} • {dayData.minutes} {t('stats.activity.minutes')}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 mt-1">{t('dashboard.weeklyProgress.legend.missed')}</p>
                    )}
                  </div>
                  {active && (
                    <div className="w-12 h-12 bg-[#3A86FF] rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 border-2 border-black/5">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Time bar indicator */}
                {active && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-[#3A86FF] h-3 rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {Math.floor(minutes / 60)}h {minutes % 60}m
                    </p>
                  </div>
                )}
              </div>

              {/* Shine effect on active days */}
              {active && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default WeeklyDetailPanel;